
import React from 'react';
import { Shield, AlertTriangle, CheckCircle, CreditCard, ArrowRight, Cpu, XCircle } from 'lucide-react';

interface AiCardVerificationProps {
  isVerifying: boolean;
  isVerified: boolean;
  verificationScore?: number;
  riskLevel?: 'low' | 'medium' | 'high';
  stripeVerified?: boolean;
  verificationError?: string;
  onVerificationComplete: (success: boolean) => void;
}

const AiCardVerification: React.FC<AiCardVerificationProps> = ({
  isVerifying,
  isVerified,
  verificationScore,
  riskLevel,
  stripeVerified = false,
  verificationError,
  onVerificationComplete
}) => {
  return (
    <div className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
      <div className="flex items-start">
        <div className="mr-3 mt-1">
          {isVerifying ? (
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : isVerified ? (
            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
          ) : verificationError ? (
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-500" />
            </div>
          ) : (
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Cpu className="h-5 w-5 text-blue-500" />
            </div>
          )}
        </div>
        
        <div>
          <h3 className="font-medium text-gray-900">
            {isVerifying ? 'Stripe AI Verification in Progress' : 
             isVerified ? 'Card Verified by Stripe AI' :
             verificationError ? 'Card Verification Failed' : 
             'Stripe AI Card Verification'}
          </h3>
          
          <p className="text-sm text-gray-500">
            {isVerifying 
              ? 'Our AI system is analyzing your card details with Stripe for security and risk assessment.'
              : isVerified 
                ? `Your card has been verified with a Stripe AI security score of ${verificationScore || 'N/A'}/100 (${riskLevel || 'unknown'} risk).`
                : verificationError
                  ? verificationError
                  : 'Enter your card details to be analyzed by our Stripe AI system for fraud prevention and security.'}
          </p>
          
          {isVerified && (
            <div className="flex items-center gap-2 mt-2 text-sm">
              {stripeVerified ? (
                <div className="flex items-center gap-2 text-blue-600">
                  <CreditCard size={16} />
                  <span>Ready for Stripe processing</span>
                  <ArrowRight size={14} />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle size={16} />
                  <span>Card not authorized by Stripe</span>
                </div>
              )}
            </div>
          )}
          
          {isVerified && verificationScore && (
            <div className="mt-3 bg-gray-100 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full ${
                  verificationScore > 80 ? 'bg-green-500' : 
                  verificationScore > 60 ? 'bg-yellow-500' : 'bg-red-500'
                }`} 
                style={{ width: `${verificationScore}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiCardVerification;
